import Container from '@mui/material/Container';
import TextField from "@mui/material/TextField";
import { Localized } from "@fluent/react";
import { ButtonGroup, Grid, MenuItem, Paper, Skeleton, Typography } from "@mui/material";
import { showError, showSuccess } from "../notifications/notificationsSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import SaveIcon from '@mui/icons-material/Save';
import { useAppSelector, useAppDispatch, useQueryCompanyId, useQueryId } from '../../app/hooks';
import { selectAllCompanies } from '../company/companiesSlice';
import { LoadCompanies } from '../../app/AppDataLoader';
import { UserStatusLabel } from './UserStatusLabel';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { createAppUser } from './administration';

export const CreateUser = () => {
    const navigate = useNavigate()

    const queryId = useQueryId();
    const queryCompanyId = useQueryCompanyId();

    const [userLogin, setUserLogin] = useState<string>("");
    const [companyId, setCompanyId] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [surname, setSurname] = useState<string>("");
    const [role, setRole] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [position, setPosition] = useState<string>("");
    const dispatch = useAppDispatch();

    //errors handling
    const [firstNameError, setFirstNameError] = useState(false);
    const [surnameError, setSurnameError] = useState(false);
    const [roleError, setRoleError] = useState(false);
    const [companyError, setCompanyError] = useState(false);
    const [emailError, setEmailError] = useState("");
    
    //load prerequisities
    const allCompanies = useAppSelector(selectAllCompanies)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if(queryCompanyId) {
            const company = allCompanies.find(f => f.id === queryCompanyId);
            if(company) {
                setFirstName(company.firstName);
                setSurname(company.lastName);
                setUserLogin(company.email);
                setPhone(company.phone);
                setRole("admin")
                setCompanyId(queryCompanyId);
            }
        }

        if(queryId) {
            setCompanyId(queryId);
        }
    }, [allCompanies, queryCompanyId, queryId])

    const handleGoBackclick = useCallback(() => navigate(-1), [navigate])

    const handleSaveClick = async () => {
        ClearErrors();
        if (!Validate()) {
            return;
        }

        try {
            setSaving(true)
            const actionResult = await dispatch(createAppUser({
                userName: userLogin, companyId, email: userLogin,
                firstName, surname: surname, role, phone, position, status: 'NOT_CONFIRMED'
            }));
            const result = unwrapResult(actionResult);

            dispatch(showSuccess("saved"));

            navigate(`/user/edit/${result.id}`, { replace: true });

        } catch (error:any) {
            if(error && error.problem) {
                dispatch(showError(error.problem.title));
            } else {
                dispatch(showError("error"));
            }
        } finally {
            setSaving(false)
        }
    }

    const validateEmail = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(userLogin)) {
            setEmailError("Nieprawidłowy adres e-mail");
        }
        else {
            setEmailError("");
        }
    };

    function ClearErrors() {
        setFirstNameError(false);
        setRoleError(false);
        setSurnameError(false);
        setCompanyError(false);
        setEmailError("");
    }

    function Validate() {
        let valResult = true;
        validateEmail()
        if (companyId === '') {
            valResult = false;
            setCompanyError(true);
        }
        if (role === '') {
            valResult = false;
            setRoleError(true);
        }
        if (firstName === '') {
            valResult = false;
            setFirstNameError(true);
        }
        if (surname === '') {
            valResult = false;
            setSurnameError(true);
        }
        if (emailError !== '') {
            valResult = false;

        }

        return valResult;
    }

    const handleUserLoginChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setUserLogin(e.target.value), [setUserLogin]);


    const handleCompanyIdChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setCompanyId(e.target.value), [setCompanyId]);

    const handleFirstNameChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setFirstName(e.target.value), [setFirstName]);

    const handleSurnameChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setSurname(e.target.value), [setSurname]);

    const handleRoleChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setRole(e.target.value), [setRole]);

    const handlePhoneChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setPhone(e.target.value), [setPhone]);

    const handlePositionChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setPosition(e.target.value), [setPosition]);

    return (
        <Container maxWidth="md" sx={{
            paddingTop: 2,
            paddingBottom: 2,
        }}>
            <Paper>
                    <Grid container spacing={2} sx={{ padding: 2 }}>
                        <Grid item xs={8}>
                            <TextField
                                fullWidth
                                required
                                inputProps={{ maxLength: 50 }}
                                value={userLogin}
                                onChange={handleUserLoginChange}
                                onBlur={validateEmail}
                                error={!!emailError}
                                helperText={emailError}
                                label={<Localized id="user-login-email">_Login / Email</Localized>}>
                            </TextField>
                        </Grid>
                        <Grid item xs={4}>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                required
                                fullWidth
                                inputProps={{ maxLength: 50 }}
                                value={firstName}
                                error={firstNameError}
                                onChange={handleFirstNameChange}
                                label={<Localized id="user-first-name">Imię</Localized>}>
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                required
                                fullWidth
                                value={surname}
                                error={surnameError}
                                inputProps={{ maxLength: 100 }}
                                onChange={handleSurnameChange}
                                label={<Localized id="user-last-name">Nazwisko</Localized>}>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                select
                                required
                                sx={{ minWidth: 256 }}
                                value={role}
                                error={roleError}
                                onChange={handleRoleChange}
                                label={<Localized id="user-role" vars={{ num : 0 }}>Rola</Localized>} >
                                {["admin", "user"].map(stage => <MenuItem key={stage} value={stage}>{stage}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                value={phone}
                                inputProps={{ maxLength: 20 }}
                                onChange={handlePhoneChange}
                                label={<Localized id="user-phone">Phone</Localized>}>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <LoadCompanies component={<Skeleton variant="rectangular" />}>
                                <TextField
                                    select
                                    fullWidth
                                    required
                                    value={companyId}
                                    error={companyError}
                                    onChange={handleCompanyIdChange}
                                    disabled={queryCompanyId !== undefined || queryId !== undefined}
                                    label={<Localized id="user-company">Firma</Localized>}>
                                    {allCompanies.map((company) => <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>)}
                                </TextField>
                            </LoadCompanies>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                value={position}
                                inputProps={{ maxLength: 200 }}
                                onChange={handlePositionChange}
                                label={<Localized id="user-position">Stanowisko</Localized>}>
                            </TextField>
                        </Grid>
                        <Grid item xs={4}>
                            <ButtonGroup color="secondary" variant="contained">
                                <LoadingButton loading={saving} startIcon={<SaveIcon />} onClick={handleSaveClick}>
                                    <Localized id="save">Zapisz</Localized>
                                </LoadingButton>
                                <LoadingButton loading={saving} onClick={handleGoBackclick} color="secondary" variant="outlined">
                                    <Localized id="back">Wróć</Localized>
                                </LoadingButton>
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                </Paper>
        </Container>
    );
}
